import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, ShieldOff, Mail, KeyRound, Copy, CheckCircle2, Fingerprint, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { decodeCreationOptions, encodeCredential, passkeysSupported } from "@/lib/webauthn";

export default function Admin2FA() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);

  // TOTP enrollment state
  const [setupData, setSetupData] = useState(null); // {secret, qr_data_url}
  const [enrollCode, setEnrollCode] = useState("");
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  // Disable flow
  const [disablePwd, setDisablePwd] = useState("");

  const loadStatus = useCallback(async () => {
    try {
      const r = await api.get("/auth/2fa/status");
      setStatus(r.data);
    } catch { /* handled by RequireAdmin */ }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const beginTotpSetup = async () => {
    try {
      const r = await api.post("/auth/2fa/setup-totp");
      setSetupData(r.data);
      setRecoveryCodes(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Setup failed");
    }
  };

  const enrollTotp = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/auth/2fa/enroll-totp", { secret: setupData.secret, code: enrollCode });
      setRecoveryCodes(r.data.recovery_codes);
      setSetupData(null);
      setEnrollCode("");
      toast.success("TOTP enabled. Save your recovery codes!");
      loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Verification failed");
    }
  };

  const enableEmail = async () => {
    try {
      await api.post("/auth/2fa/enable-email");
      toast.success("Email 2FA enabled.");
      loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to enable email 2FA");
    }
  };

  const disableAll = async (e) => {
    e.preventDefault();
    if (!window.confirm("Turn off both TOTP and email 2FA?")) return;
    try {
      await api.post("/auth/2fa/disable", { password: disablePwd });
      toast.success("2FA disabled.");
      setDisablePwd("");
      setRecoveryCodes(null);
      loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to disable");
    }
  };

  const copyRecovery = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodes.join("\n"));
      toast.success("Recovery codes copied");
    } catch (err) {
      console.error("Copy recovery failed", err);
      toast.error("Copy failed — select & copy manually.");
    }
  };

  const enrollPasskey = async () => {
    if (!passkeysSupported()) {
      toast.error("This browser doesn't support passkeys.");
      return;
    }
    try {
      const begin = await api.post("/auth/2fa/passkey/register-begin");
      const cred = await navigator.credentials.create(decodeCreationOptions(begin.data));
      const nickname = window.prompt("Name this passkey (e.g. 'MacBook Touch ID')") || "Passkey";
      await api.post("/auth/2fa/passkey/register-finish", { credential: encodeCredential(cred), nickname });
      toast.success("Passkey enrolled.");
      loadStatus();
    } catch (err) {
      console.error("Passkey enrollment failed", err);
      toast.error(err?.response?.data?.detail || err?.message || "Enrollment failed");
    }
  };

  const removePasskey = async (credId) => {
    if (!window.confirm("Remove this passkey?")) return;
    try {
      await api.delete(`/auth/2fa/passkey/${encodeURIComponent(credId)}`);
      toast.success("Passkey removed");
      loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove");
    }
  };

  if (!status) {
    return <div className="max-w-3xl mx-auto px-6 py-20 font-mono-plex text-sm uppercase tracking-wider text-[#555]">Loading…</div>;
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      <Link to="/admin" className="inline-flex items-center gap-2 font-mono-plex text-sm uppercase tracking-wider hover:underline" data-testid="back-to-admin">
        <ArrowLeft className="w-4 h-4" /> Back to admin
      </Link>

      <div className="mt-4 border-b border-black/15 pb-6">
        <div className="overline">/security</div>
        <h1 className="font-display text-4xl font-black tracking-tighter mt-2">Two-factor authentication</h1>
        <p className="text-base text-[#333] mt-3 max-w-xl leading-relaxed">
          Add a second step to sign-in. Pick an authenticator app, an email code, or both —
          you can switch any time. Signed in as <b>{user?.email}</b>.
        </p>
      </div>

      {/* Status strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-black/15 border border-black/15 mt-6">
        <StatusTile label="Authenticator app" enabled={status.totp_enabled} testid="status-totp" />
        <StatusTile label="Email code" enabled={status.email_2fa_enabled} testid="status-email" />
        <div className="bg-white p-4">
          <div className="font-mono-plex text-[11px] uppercase tracking-wider text-[#555]">Recovery codes left</div>
          <div className="font-display text-3xl font-bold mt-1" data-testid="status-recovery-count">
            {status.recovery_codes_remaining}
            <span className="text-base text-[#555]"> / 8</span>
          </div>
        </div>
      </div>

      {/* Recovery codes (one-time display after enrollment) */}
      {recoveryCodes && (
        <div className="mt-8 border-2 border-[#008A00] bg-[#F1F8F1] p-6" data-testid="recovery-codes-block">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-[#008A00] shrink-0 mt-1" />
            <div className="flex-1">
              <div className="font-display text-xl font-bold">Save these recovery codes</div>
              <p className="text-base text-[#333] mt-2">
                Store them somewhere safe (password manager). Each one is a one-time bypass if you ever lose your authenticator. You won't see them again.
              </p>
              <pre className="mt-4 bg-white border border-black p-4 font-mono-plex text-sm leading-7 select-all">
                {recoveryCodes.join("\n")}
              </pre>
              <Button onClick={copyRecovery} className="mt-3 rounded-none bg-black text-white h-10" data-testid="copy-recovery-btn">
                <Copy className="w-4 h-4 mr-2" /> Copy all codes
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* TOTP enrollment */}
      {!status.totp_enabled && !setupData && (
        <div className="mt-8 border border-black/15 bg-white p-6">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" />
            <h2 className="font-display text-2xl font-bold">Add authenticator app (TOTP)</h2>
          </div>
          <p className="text-base text-[#333] mt-2">
            Works with Google Authenticator, Authy, 1Password, Bitwarden, etc.
          </p>
          <Button onClick={beginTotpSetup} className="mt-4 rounded-none bg-black text-white h-11" data-testid="setup-totp-btn">
            <KeyRound className="w-4 h-4 mr-2" /> Set up authenticator
          </Button>
        </div>
      )}

      {setupData && (
        <div className="mt-8 border-2 border-black bg-white p-6" data-testid="totp-enroll-block">
          <h2 className="font-display text-2xl font-bold">1. Scan this QR code</h2>
          <div className="mt-4 grid md:grid-cols-2 gap-6 items-start">
            <img src={setupData.qr_data_url} alt="TOTP QR code" className="border border-black/15 w-full max-w-[280px]" />
            <div>
              <p className="text-base text-[#333]">Or enter this secret manually:</p>
              <code className="mt-2 block bg-[#F5F5F5] border border-black/15 p-3 font-mono-plex text-base break-all select-all">
                {setupData.secret}
              </code>
            </div>
          </div>
          <form onSubmit={enrollTotp} className="mt-6">
            <h3 className="font-display text-xl font-bold">2. Enter the 6-digit code your app shows</h3>
            <div className="mt-3 flex gap-3 max-w-md">
              <Input
                data-testid="enroll-code-input"
                value={enrollCode}
                onChange={(e) => setEnrollCode(e.target.value)}
                placeholder="123 456"
                required
                autoFocus
                className="rounded-none border-black/30 h-12 text-lg font-mono-plex tracking-widest"
              />
              <Button type="submit" data-testid="enroll-submit-btn" className="rounded-none bg-black text-white h-12 px-6" disabled={!enrollCode}>
                Verify
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* Email 2FA */}
      {!status.email_2fa_enabled && (
        <div className="mt-8 border border-black/15 bg-white p-6">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            <h2 className="font-display text-2xl font-bold">Add email code</h2>
          </div>
          <p className="text-base text-[#333] mt-2">
            We'll email a 6-digit code at sign-in, valid 10 minutes. Easier than an authenticator — works on any device.
          </p>
          <Button onClick={enableEmail} className="mt-4 rounded-none bg-black text-white h-11" data-testid="enable-email-btn">
            <Mail className="w-4 h-4 mr-2" /> Enable email codes
          </Button>
        </div>
      )}

      {/* Passkeys / WebAuthn */}
      <div className="mt-8 border border-black/15 bg-white p-6">
        <div className="flex items-center gap-2">
          <Fingerprint className="w-5 h-5" />
          <h2 className="font-display text-2xl font-bold">Passkeys</h2>
          {status.passkey_enabled && (
            <span className="ml-2 inline-block px-2 py-0.5 bg-[#008A00] text-white font-mono-plex text-[11px] uppercase tracking-wider">Active</span>
          )}
        </div>
        <p className="text-base text-[#333] mt-2">
          Use Touch ID, Face ID, Windows Hello, or a hardware key (YubiKey) as a phishing-resistant
          third 2FA method. Browser-native, no app required.
        </p>

        {(status.passkeys || []).length > 0 && (
          <ul className="mt-4 space-y-2" data-testid="passkey-list">
            {status.passkeys.map((p) => (
              <li key={p.cred_id} className="flex items-center justify-between border border-black/15 p-3">
                <div>
                  <div className="font-display font-bold">{p.nickname}</div>
                  <div className="font-mono-plex text-[11px] text-[#555] mt-0.5">
                    Added {p.added_at?.slice(0, 10)} · {p.cred_id.slice(0, 16)}…
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removePasskey(p.cred_id)}
                  data-testid={`remove-passkey-${p.cred_id.slice(0, 8)}`}
                  className="rounded-none border-[#D32F2F] text-[#D32F2F] h-9"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Remove
                </Button>
              </li>
            ))}
          </ul>
        )}

        <Button onClick={enrollPasskey} className="mt-4 rounded-none bg-black text-white h-11" data-testid="enroll-passkey-btn">
          <Fingerprint className="w-4 h-4 mr-2" /> Add a passkey
        </Button>
      </div>

      {/* Disable */}
      {(status.totp_enabled || status.email_2fa_enabled) && (
        <div className="mt-8 border border-[#D32F2F]/40 bg-[#FDF5F5] p-6">
          <div className="flex items-center gap-2">
            <ShieldOff className="w-5 h-5 text-[#D32F2F]" />
            <h2 className="font-display text-2xl font-bold">Disable 2FA</h2>
          </div>
          <p className="text-base text-[#333] mt-2">
            Turns off both TOTP and email codes, and invalidates remaining recovery codes. Re-enter your password to confirm.
          </p>
          <form onSubmit={disableAll} className="mt-4 flex gap-3 max-w-md">
            <Input
              data-testid="disable-password-input"
              type="password"
              value={disablePwd}
              onChange={(e) => setDisablePwd(e.target.value)}
              placeholder="Current password"
              required
              className="rounded-none border-black/30 h-11"
            />
            <Button type="submit" variant="outline" className="rounded-none border-[#D32F2F] text-[#D32F2F] h-11" data-testid="disable-submit-btn">
              Disable
            </Button>
          </form>
        </div>
      )}
    </div>
  );
}

function StatusTile({ label, enabled, testid }) {
  return (
    <div className={`p-4 ${enabled ? "bg-[#008A00] text-white" : "bg-white"}`} data-testid={testid}>
      <div className={`font-mono-plex text-[11px] uppercase tracking-wider ${enabled ? "text-white/70" : "text-[#555]"}`}>
        {label}
      </div>
      <div className="font-display text-2xl font-bold mt-1">{enabled ? "ON" : "OFF"}</div>
    </div>
  );
}
